package features.auth.validation

import kotlinx.browser.document
import kotlinx.browser.window
import features.auth.domain.User
import features.auth.data.UserService
import features.auth.data.UserCheckResult
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

/**
 * User Validation for Add Member
 */

var currentValidationEmail: String = ""
var currentValidationResult: UserCheckResult? = null

/**
 * Initialize validation - expose function to window for HTML access
 */
fun initUserValidation() {
    window.asDynamic().validateUserEmail = ::validateUserEmail
    console.log("✅ validateUserEmail exposed to window")
}

/**
 * Validate user email in real-time
 */
fun validateUserEmail(email: String) {
    console.log("🔍 validateUserEmail called with: $email")
    
    val messageEl = document.getElementById("email-validation-message") as? HTMLElement
    val addButton = document.getElementById("add-member-button") as? HTMLElement
    
    if (email.isBlank()) {
        messageEl?.classList?.add("hidden")
        addButton?.setAttribute("disabled", "true")
        currentValidationResult = null
        return
    }
    
    // Basic email format check
    if (!isValidEmailFormat(email)) {
        showValidationMessage("⚠️ Format email tidak valid", "text-amber-400", false)
        addButton?.setAttribute("disabled", "true")
        currentValidationResult = null
        return
    }
    
    // Show loading
    showValidationMessage("🔍 Checking user...", "text-slate-400", false)
    addButton?.setAttribute("disabled", "true")
    
    currentValidationEmail = email
    
    // Check if user exists
    UserService.checkUserExists(email)
        .then { result ->
            console.log("📊 Check result - exists: ${result.exists}, user: ${result.user?.email}")
            
            // Only update if this is still the current email being validated
            if (email == currentValidationEmail) {
                currentValidationResult = result
                
                if (result.exists && result.user != null) {
                    // Check if user is already a member of current team
                    val teamId = AppState.currentTeam?.id
                    val existingMembers = AppState.members[teamId] ?: emptyList()
                    val isDuplicate = existingMembers.any { it.email == email || it.id == result.user!!.id }
                    
                    console.log("🔍 Duplicate check - teamId: $teamId, isDuplicate: $isDuplicate")
                    
                    if (isDuplicate) {
                        showValidationMessage("❌ User sudah menjadi member team ini", "text-red-400", false)
                        addButton?.setAttribute("disabled", "true")
                        currentValidationResult = null
                    } else {
                        showValidationSuccess(result.user!!)
                        addButton?.removeAttribute("disabled")
                    }
                } else {
                    showValidationError()
                    addButton?.setAttribute("disabled", "true")
                }
            }
        }
        .catch { error ->
            console.error("Validation error:", error)
            showValidationMessage("❌ Error checking user", "text-red-400", false)
            addButton?.setAttribute("disabled", "true")
            currentValidationResult = null
        }
}

/**
 * Show validation success message
 */
fun showValidationSuccess(user: User) {
    val message = "✅ ${user.name} (${user.email})"
    showValidationMessage(message, "text-emerald-400", true)
}

/**
 * Show validation error message
 */
fun showValidationError() {
    val message = "❌ User belum terdaftar. Minta user login dulu dengan email ini."
    showValidationMessage(message, "text-red-400", false)
}

/**
 * Show validation message
 */
fun showValidationMessage(message: String, colorClass: String, isSuccess: Boolean) {
    val messageEl = document.getElementById("email-validation-message") as? HTMLElement
    
    if (messageEl != null) {
        messageEl.textContent = message
        messageEl.className = "mt-1 text-sm $colorClass"
        messageEl.classList.remove("hidden")
    }
}

/**
 * Hide validation message
 */
fun hideValidationMessage() {
    val messageEl = document.getElementById("email-validation-message") as? HTMLElement
    messageEl?.classList?.add("hidden")
}

/**
 * Basic email format validation
 */
fun isValidEmailFormat(email: String): Boolean {
    val regex = Regex("^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")
    return regex.matches(email)
}
